import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { mediaUrl, PLACEHOLDER_COVER } from "@/lib/media";
import { Check, X, ChevronRight, Coins, CalendarDays, Info, FileText, ClipboardCheck } from "lucide-react";
import { Button } from "./ui/Button";
import { Modal } from "./ui/Modal";
import { Avatar, IconTile } from "./ui/Layout";
import { Badge, StatusBadge, DECISION_STATUS, DECISION_KIND } from "./ui/Badge";
import { Field, Textarea } from "./ui/Field";
import { fmtDateLong, fmtDateTime, daysUntil } from "./format";

export const NEEDS_APPROVAL = new Set(["approval", "decision", "selection", "validation", "change_request"]);
const TITLES = { approved: "Confirmi aprobarea?", rejected: "Confirmi respingerea?", acknowledged: "Confirmi luarea la cunoștință?" };
const CTA = { approved: "Confirmă aprobarea", rejected: "Confirmă respingerea", acknowledged: "Confirmă" };
const OK_MSG = { approved: "Aprobare înregistrată", rejected: "Respingere înregistrată", acknowledged: "Confirmare înregistrată" };

export async function respondToAction(a, decision, comment) {
  await api.post(`/client/projects/${a.project_id}/actions/${a.id}/respond`, { decision, comment: (comment || "").trim() });
}

/** Rând din lista de decizii (coloana stângă). */
export function DecisionRow({ a, selected, onSelect }) {
  const pending = a.status === "pending";
  const overdue = pending && a.deadline && daysUntil(a.deadline) < 0;
  return (
    <button type="button" onClick={onSelect} aria-pressed={selected} data-testid={`decision-${a.id}`}
      className={`w-full text-left cp-panel p-4 flex items-start gap-3 transition-colors ${pending ? "border-cp-warning-border bg-cp-warning-soft/60" : ""} ${selected ? "ring-2 ring-cp-accent border-transparent" : "hover:border-cp-border-strong"}`}>
      <IconTile icon={pending ? FileText : ClipboardCheck} tone={pending ? "amber" : "neutral"} />
      <div className="flex-1 min-w-0">
        <div className="text-[14.5px] font-semibold text-cp-text leading-snug">{DECISION_KIND[a.kind] || a.title}</div>
        <div className="text-[13px] text-cp-text-2 mt-0.5 truncate">{a.project_name}</div>
        <div className="cp-meta mt-1.5 flex flex-wrap gap-x-2">
          {a.cost_impact && <span>Impact: {a.cost_impact}</span>}{a.cost_impact && a.schedule_impact && <span>|</span>}{a.schedule_impact && <span>{a.schedule_impact}</span>}
          {!a.cost_impact && !a.schedule_impact && <span className="truncate">{a.title}</span>}
        </div>
      </div>
      <div className="text-right shrink-0 pl-2">
        {pending ? (
          <><div className="cp-meta">Termen limită</div><div className={`text-[13px] font-semibold mt-0.5 ${overdue ? "text-cp-danger" : "text-cp-text"}`}>{a.deadline ? fmtDateLong(a.deadline) : "—"}</div></>
        ) : <StatusBadge map={DECISION_STATUS} value={a.status} size="sm" />}
        <ChevronRight className="h-4 w-4 text-cp-text-3 ml-auto mt-1.5" aria-hidden="true" />
      </div>
    </button>
  );
}

/** Panoul central: detaliul solicitării + comentariu + acțiuni. */
export function DecisionDetail({ a, pm, comment, setComment, onIntent, intent }) {
  const pending = a.status === "pending";
  const approval = NEEDS_APPROVAL.has(a.kind);
  return (
    <article className="cp-panel p-5 sm:p-6" data-testid="decision-detail" aria-labelledby="dec-title">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="dec-title" className="cp-h1 !text-[26px]">{DECISION_KIND[a.kind] || "Solicitare"}</h2>
          <div className="text-[13.5px] text-cp-text-2 mt-1 flex items-center gap-2 flex-wrap"><Link to={`/portal/proiecte/${a.project_id}?tab=decizii`} className="text-cp-accent hover:underline">{a.project_name}</Link>{a.project_code && <><span className="text-cp-text-3">|</span><span>{a.project_code}</span></>}</div>
        </div>
        <StatusBadge map={DECISION_STATUS} value={a.status} fallback="pending" noIcon className="shrink-0" />
      </div>

      <div className="mt-4 flex items-center gap-3">
        <Avatar name={pm?.name || a.created_by || "Brisk"} size="md" />
        <div className="min-w-0"><div className="text-[14px] font-semibold text-cp-text truncate">{pm?.name || a.created_by || "Echipa Brisk"}</div><div className="cp-meta">{pm?.job_title || "Project Manager"} · {fmtDateLong(a.created_at)}</div></div>
      </div>

      <div className="mt-4">
        <p className="text-[15px] font-medium text-cp-text">{a.title}</p>
        <p className="cp-body mt-1 whitespace-pre-line">{a.description || "Solicitare transmisă de echipa de proiect."}</p>
        {a.recommendation && <div className="mt-3 cp-panel-soft px-4 py-3 text-[13.5px] text-cp-text-2"><span className="font-semibold text-cp-text">Recomandarea Brisk: </span>{a.recommendation}</div>}
      </div>

      {(a.cost_impact || a.schedule_impact) && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {a.cost_impact && <ImpactCard icon={Coins} label="Impact asupra costurilor" value={a.cost_impact} />}
          {a.schedule_impact && <ImpactCard icon={CalendarDays} label="Impact asupra programului" value={a.schedule_impact} />}
        </div>
      )}

      {pending ? (
        <>
          <div className="mt-5">
            <Field label="Comentariu (opțional)">
              {(p) => <Textarea {...p} value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Adaugă un comentariu..." data-testid="respond-comment" />}
            </Field>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {approval ? (
              <>
                <Button onClick={() => onIntent("approved")} data-testid={`approve-${a.id}`} aria-pressed={intent === "approved"} className="min-w-[190px]"><Check /> Analizează și aprobă</Button>
                <Button variant="outline" onClick={() => onIntent("rejected")} data-testid={`reject-${a.id}`} aria-pressed={intent === "rejected"} className="min-w-[140px]"><X /> Respinge</Button>
              </>
            ) : <Button onClick={() => onIntent("acknowledged")} data-testid={`ack-${a.id}`}><Check /> Confirmă că am luat la cunoștință</Button>}
            <span className="cp-meta inline-flex items-center gap-1.5 sm:ml-auto"><Info className="h-3.5 w-3.5" aria-hidden="true" /> Verifică detaliile înainte de confirmare.</span>
          </div>
        </>
      ) : (
        <div className="mt-5 cp-panel-soft px-4 py-3.5 text-[13.5px]">
          <span className="font-semibold text-cp-text">{DECISION_STATUS[a.status]?.label || a.status}</span> de <span className="font-medium text-cp-text">{a.responded_by}</span> · {fmtDateTime(a.responded_at)}
          {a.client_comment && <p className="mt-1 text-cp-text-2">„{a.client_comment}”</p>}
        </div>
      )}
    </article>
  );
}

function ImpactCard({ icon: Icon, label, value }) {
  return (
    <div className="cp-panel-amber px-4 py-3.5 flex items-center gap-3">
      <Icon className="h-5 w-5 text-cp-amber shrink-0" aria-hidden="true" />
      <div className="min-w-0"><div className="text-[12.5px] text-cp-text-2">{label}</div><div className="cp-serif text-[22px] text-cp-text leading-tight">{value}</div></div>
    </div>
  );
}

/** Coloana dreaptă: imaginea proiectului + panou de confirmare / registru. */
export function ConfirmPanel({ a, project, intent, comment, onBack, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const cover = project?.cover_image;

  async function submit() {
    if (intent === "rejected" && !comment.trim()) { setErr("Pentru respingere este necesar un motiv în câmpul de comentariu."); return; }
    setBusy(true); setErr("");
    try { await respondToAction(a, intent, comment); toast.success(OK_MSG[intent]); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  }

  return (
    <div className="cp-panel overflow-hidden" data-testid="confirm-panel">
      <div className="aspect-[16/10] bg-cp-bg-subtle"><img src={mediaUrl(cover) || PLACEHOLDER_COVER} alt="" className="h-full w-full object-cover" /></div>
      <div className="p-5">
        {intent ? (
          <>
            <h3 className="cp-h2 !text-[20px]">{TITLES[intent]}</h3>
            <p className="cp-body-sm mt-1">Această decizie va fi înregistrată în proiect, cu numele tău și data.</p>
            {(a.cost_impact || a.schedule_impact) && (
              <div className="mt-4 grid grid-cols-2 gap-3">
                {a.cost_impact && <Mini icon={Coins} label="Cost" value={a.cost_impact} />}
                {a.schedule_impact && <Mini icon={CalendarDays} label="Program" value={a.schedule_impact} />}
              </div>
            )}
            {comment.trim() && <p className="mt-3 cp-meta">Comentariu: „{comment.trim()}”</p>}
            {err && <p role="alert" className="mt-3 text-[12.5px] text-cp-danger" data-testid="confirm-error">{err}</p>}
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={onBack} disabled={busy} data-testid="confirm-back">Înapoi</Button>
              <Button variant={intent === "rejected" ? "danger" : "primary"} onClick={submit} loading={busy} data-testid="respond-submit">{CTA[intent]}</Button>
            </div>
          </>
        ) : (
          <>
            <h3 className="cp-h2 !text-[20px]">{project?.name || a.project_name}</h3>
            <p className="cp-meta mt-0.5">{a.project_code}{project?.current_stage ? ` · ${project.current_stage}` : ""}</p>
            {(a.log || []).length > 0 ? (
              <ol className="mt-4 space-y-2.5" data-testid={`log-${a.id}`}>
                <li className="cp-label">Registru decizii</li>
                {a.log.map((e, i) => (
                  <li key={i} className="text-[12.5px] text-cp-text-2 flex gap-2"><Badge tone="neutral" size="sm">v{e.version}</Badge><span><span className="font-medium text-cp-text">{e.who}</span> · {DECISION_STATUS[e.what]?.label?.toLowerCase() || e.what}{e.comment ? ` · „${e.comment}”` : ""}<br /><span className="text-cp-text-3">{fmtDateTime(e.when)}</span></span></li>
                ))}
              </ol>
            ) : a.status === "pending" ? <p className="cp-body-sm mt-4">Alege „Analizează și aprobă” sau „Respinge” pentru a continua. Vei putea confirma decizia aici.</p> : null}
          </>
        )}
      </div>
    </div>
  );
}

function Mini({ icon: Icon, label, value }) {
  return <div className="cp-panel px-3 py-2.5 flex items-center gap-2.5 min-w-0"><Icon className="h-4 w-4 text-cp-text-3 shrink-0" aria-hidden="true" /><div className="min-w-0"><div className="cp-meta">{label}</div><div className="text-[15px] font-semibold text-cp-text truncate">{value}</div></div></div>;
}

/** Modal de răspuns (folosit în detaliul proiectului). */
export function RespondModal({ action: a, decision, onClose, onDone }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const open = !!decision;

  async function submit() {
    if (decision === "rejected" && !comment.trim()) { setErr("Pentru respingere este necesar un motiv."); return; }
    setBusy(true); setErr("");
    try { await respondToAction(a, decision, comment); toast.success(OK_MSG[decision]); setComment(""); onClose(); onDone && onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  }

  return (
    <Modal open={open} onOpenChange={(v) => !v && onClose()} title={TITLES[decision] || ""} testid="respond-modal" description={a.title}
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={busy}>Renunță</Button>
        <Button variant={decision === "rejected" ? "danger" : "primary"} onClick={submit} loading={busy} data-testid="respond-submit">{CTA[decision] || "Confirmă"}</Button>
      </>}>
      <div className="space-y-4">
        {(a.cost_impact || a.schedule_impact) && (
          <div className="grid grid-cols-2 gap-3">
            {a.cost_impact && <Mini icon={Coins} label="Impact cost" value={a.cost_impact} />}
            {a.schedule_impact && <Mini icon={CalendarDays} label="Impact program" value={a.schedule_impact} />}
          </div>
        )}
        <Field label={decision === "rejected" ? "Motivul respingerii" : "Comentariu (opțional)"} required={decision === "rejected"} error={err}
          hint="Comentariul este înregistrat în registrul de decizii și transmis Project Managerului.">
          {(p) => <Textarea {...p} value={comment} onChange={(e) => { setComment(e.target.value); setErr(""); }} rows={3} data-testid="respond-comment" />}
        </Field>
        <p className="cp-meta">Decizia se înregistrează cu numele tău, data și ora. Nu poate fi ștearsă ulterior.</p>
      </div>
    </Modal>
  );
}
